import React from 'react';
import { motion, Variants } from 'framer-motion';
import { GraduationCap, Sparkles, Zap, ShieldCheck, Smile } from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';
// import { AnimatedSection } from './AnimatedSection'; // Assuming this is defined externally

interface VibeSectionProps {
  data: LivingInAreaData['areaVibe'];
}

// --- Animation Variants (Staggered fade-up for cards) ---
const cardContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardItemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

// --- VibeCard Component (Updated for high contrast) ---
const VibeCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}> = ({ icon, label, value, color }) => (
  <motion.div
    variants={cardItemVariants}
    whileHover={{
      y: -6,
      boxShadow:
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    }}
    // Explicit background colors for high contrast
    className="rounded-3xl border border-slate-200 bg-white p-6 shadow-md transition-all duration-300"
  >
    <div className={`mb-3 flex items-center gap-3 ${color}`}>
      {icon}
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
        {label}
      </span>
    </div>
    <p className="text-xl font-extrabold capitalize text-slate-900">
      {(value || 'Not specified').trim()}
    </p>
  </motion.div>
);

export const VibeSection: React.FC<VibeSectionProps> = ({ data }) => {
  return (
    <section className="max-w-7xl mx-auto px-4">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={cardContainerVariants}
        // Main container uses a fixed, high-contrast background (light gray/dark blue)
        className="relative overflow-hidden rounded-[2.5rem] border border-slate-800 bg-slate-950 p-8 shadow-xl md:p-12"
      >
        {/* Decorative background element with gradient highlight */}
        <div
          className="absolute inset-0 opacity-10 pointer-events-none 
                        bg-gradient-to-tr from-indigo-500/10 via-slate-900 to-slate-950"
        />

        <motion.div
          className="absolute top-6 right-6 opacity-40 text-indigo-400"
          animate={{ rotate: [0, 180, 360], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        >
          <Smile size={80} />
        </motion.div>

        <div className="relative z-10">
          <h2 className="mb-10 text-4xl font-extrabold text-white md:text-5xl">
            Area Vibe
          </h2>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <VibeCard
              icon={<GraduationCap size={24} />}
              label="Education Level"
              value={data.educationLevel}
              color="text-blue-600"
            />
            <VibeCard
              icon={<Sparkles size={24} />}
              label="Overall Feel"
              value={data.feel}
              color="text-indigo-600"
            />
            <VibeCard
              icon={<Zap size={24} />}
              label="Social Energy"
              value={data.socialEnergy}
              color="text-pink-600"
            />
            {data.safetyPerception && (
              <VibeCard
                icon={<ShieldCheck size={24} />}
                label="Safety Perception"
                value={data.safetyPerception}
                color="text-green-600"
              />
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
};
